import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2, CheckCircle2, ArrowLeft, Landmark, Search } from "lucide-react";
import api, { errMsg } from "../lib/api";
import { formatRupiah, maskAccount } from "../lib/format";
import { AmountInput, QuickAmounts } from "../components/AmountInput";
import { PinDialog } from "../components/PinDialog";

const steps = ["Tujuan", "Nominal", "Konfirmasi"];

export default function TransferBank() {
  const navigate = useNavigate();
  const location = useLocation();
  const prefill = location.state?.account || null;

  const [step, setStep] = useState(prefill ? 1 : 0);
  const [banks, setBanks] = useState([]);
  const [saved, setSaved] = useState([]);
  const [bankCode, setBankCode] = useState(prefill?.bank_code || "");
  const [accountNumber, setAccountNumber] = useState(prefill?.account_number || "");
  const [inquiry, setInquiry] = useState(prefill ? { bank_code: prefill.bank_code, bank_name: prefill.bank_name, account_number: prefill.account_number, account_holder: prefill.account_holder } : null);
  const [inquiring, setInquiring] = useState(false);
  const [amount, setAmount] = useState(0);
  const [note, setNote] = useState("");
  const [saveAccount, setSaveAccount] = useState(false);
  const [wallet, setWallet] = useState(null);
  const [pinOpen, setPinOpen] = useState(false);
  const [idemKey, setIdemKey] = useState("");

  useEffect(() => {
    api.get("/banks").then((r) => setBanks(r.data));
    api.get("/bank-accounts").then((r) => setSaved(r.data)).catch(() => {});
    api.get("/wallet").then((r) => setWallet(r.data));
  }, []);

  useEffect(() => {
    if (step === 2) setIdemKey(crypto.randomUUID());
  }, [step]);

  const doInquiry = async () => {
    if (!bankCode || !accountNumber) {
      toast.error("Pilih bank dan isi nomor rekening dulu");
      return;
    }
    setInquiring(true);
    setInquiry(null);
    try {
      const res = await api.post("/banks/inquiry", { bank_code: bankCode, account_number: accountNumber });
      setInquiry(res.data);
    } catch (err) {
      toast.error(errMsg(err, "Rekening tidak ditemukan"));
    } finally {
      setInquiring(false);
    }
  };

  const pickSaved = (acc) => {
    setBankCode(acc.bank_code);
    setAccountNumber(acc.account_number);
    setInquiry({ bank_code: acc.bank_code, bank_name: acc.bank_name, account_number: acc.account_number, account_holder: acc.account_holder });
  };

  const goNominal = () => {
    if (!inquiry) {
      toast.error("Cek nama pemilik rekening terlebih dahulu");
      return;
    }
    setStep(1);
  };

  const goConfirm = () => {
    if (amount < 10000) {
      toast.error("Minimal transfer ke bank Rp 10.000");
      return;
    }
    if (wallet && amount > wallet.balance) {
      toast.error("Saldo tidak cukup");
      return;
    }
    setStep(2);
  };

  const confirmWithPin = async (pin) => {
    const res = await api.post("/transfers/bank", {
      bank_code: inquiry.bank_code,
      account_number: inquiry.account_number,
      account_holder: inquiry.account_holder,
      amount,
      note,
      pin,
      idempotency_key: idemKey,
      save_account: saveAccount,
    });
    setPinOpen(false);
    toast.success("Transfer sedang diproses!");
    navigate(`/transaksi/${res.data.id}`);
  };

  return (
    <div className="max-w-xl mx-auto space-y-5">
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="icon" onClick={() => (step === 0 ? navigate("/transfer") : setStep(step - 1))} data-testid="transfer-bank-back-button" aria-label="Kembali">
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <div>
          <h1 className="font-heading text-xl font-semibold">Transfer ke Bank</h1>
          <p className="text-xs text-muted-foreground">Gratis biaya admin</p>
        </div>
      </div>

      {/* Stepper */}
      <div className="flex items-center gap-2">
        {steps.map((s, i) => (
          <React.Fragment key={s}>
            <div className={`flex items-center gap-1.5 ${i <= step ? "text-primary" : "text-muted-foreground"}`}>
              <span className={`h-6 w-6 rounded-full text-xs font-semibold flex items-center justify-center ${i < step ? "bg-primary text-white" : i === step ? "bg-primary/10 text-primary border border-primary" : "bg-secondary"}`}>
                {i + 1}
              </span>
              <span className="text-xs font-medium hidden sm:inline">{s}</span>
            </div>
            {i < steps.length - 1 && <div className={`h-px flex-1 ${i < step ? "bg-primary" : "bg-border"}`} />}
          </React.Fragment>
        ))}
      </div>

      {step === 0 && (
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="space-y-4">
          {saved.length > 0 && (
            <Card className="rounded-2xl">
              <CardContent className="p-4">
                <p className="text-sm font-semibold mb-3">Rekening Tersimpan</p>
                <div className="flex gap-2 overflow-x-auto pb-1">
                  {saved.map((acc) => (
                    <button
                      key={acc.id}
                      onClick={() => pickSaved(acc)}
                      data-testid="saved-account-chip"
                      className={`shrink-0 rounded-xl border px-3 py-2 text-left hover:border-primary/40 transition-[border-color] duration-200 ${inquiry?.account_number === acc.account_number && bankCode === acc.bank_code ? "border-primary bg-primary/5" : ""}`}
                    >
                      <p className="text-xs font-semibold">{acc.account_holder}</p>
                      <p className="text-[10px] text-muted-foreground">{acc.bank_name} · {maskAccount(acc.account_number)}</p>
                    </button>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
          <Card className="rounded-2xl">
            <CardContent className="p-5 space-y-4">
              <div className="space-y-1.5">
                <Label>Bank Tujuan</Label>
                <Select value={bankCode} onValueChange={(v) => { setBankCode(v); setInquiry(null); }}>
                  <SelectTrigger className="h-11" data-testid="transfer-bank-select">
                    <SelectValue placeholder="Pilih bank" />
                  </SelectTrigger>
                  <SelectContent>
                    {banks.map((b) => (
                      <SelectItem key={b.code} value={b.code} data-testid={`bank-option-${b.code}`}>{b.name} — {b.full_name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label>Nomor Rekening</Label>
                <Input
                  data-testid="transfer-account-number-input"
                  className="h-11 font-mono"
                  inputMode="numeric"
                  placeholder="Contoh: 1234567890"
                  value={accountNumber}
                  onChange={(e) => { setAccountNumber(e.target.value.replace(/\D/g, "")); setInquiry(null); }}
                />
              </div>
              <Button variant="outline" className="w-full border-primary text-primary hover:bg-primary/5" onClick={doInquiry} disabled={inquiring} data-testid="transfer-check-name-button">
                {inquiring ? <Loader2 className="h-4 w-4 animate-spin" /> : (<><Search className="h-4 w-4 mr-1.5" /> Cek Nama</>)}
              </Button>
              {inquiry && (
                <motion.div initial={{ opacity: 0, scale: 0.97 }} animate={{ opacity: 1, scale: 1 }} className="rounded-xl border border-emerald-200 bg-emerald-50 p-4 flex items-center gap-3" data-testid="inquiry-result-card">
                  <CheckCircle2 className="h-6 w-6 text-emerald-600 shrink-0" />
                  <div>
                    <p className="font-semibold text-sm" data-testid="inquiry-account-holder">{inquiry.account_holder}</p>
                    <p className="text-xs text-muted-foreground">{inquiry.bank_name} · {maskAccount(inquiry.account_number)}</p>
                  </div>
                </motion.div>
              )}
              <Button className="w-full h-11" onClick={goNominal} disabled={!inquiry} data-testid="transfer-continue-button">
                Lanjut
              </Button>
            </CardContent>
          </Card>
        </motion.div>
      )}

      {step === 1 && (
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="space-y-4">
          <Card className="rounded-2xl">
            <CardContent className="p-5 space-y-4">
              <div className="rounded-xl bg-secondary p-3 flex items-center gap-3">
                <span className="h-9 w-9 rounded-full bg-blue-50 text-primary flex items-center justify-center"><Landmark className="h-4 w-4" /></span>
                <div>
                  <p className="text-sm font-semibold">{inquiry?.account_holder}</p>
                  <p className="text-xs text-muted-foreground">{inquiry?.bank_name} · {maskAccount(inquiry?.account_number)}</p>
                </div>
              </div>
              <div className="space-y-1.5">
                <Label>Nominal Transfer</Label>
                <AmountInput value={amount} onChange={setAmount} testId="transfer-amount-input" />
                <p className="text-xs text-muted-foreground">
                  Saldo kamu: <span className="font-semibold tabular-nums">{wallet ? formatRupiah(wallet.balance) : "..."}</span> · Minimal Rp 10.000
                </p>
              </div>
              <QuickAmounts onPick={setAmount} />
              <div className="space-y-1.5">
                <Label>Catatan (opsional)</Label>
                <Input data-testid="transfer-note-input" className="h-11" placeholder="Contoh: bayar kos" value={note} onChange={(e) => setNote(e.target.value)} maxLength={60} />
              </div>
              <Button className="w-full h-11" onClick={goConfirm} disabled={amount < 10000} data-testid="transfer-to-confirm-button">
                Lanjut
              </Button>
            </CardContent>
          </Card>
        </motion.div>
      )}

      {step === 2 && (
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="space-y-4">
          <Card className="rounded-2xl">
            <CardContent className="p-5 space-y-4">
              <h3 className="font-heading font-semibold">Ringkasan Transfer</h3>
              <div className="space-y-2.5 text-sm">
                <div className="flex justify-between"><span className="text-muted-foreground">Penerima</span><span className="font-medium text-right">{inquiry?.account_holder}</span></div>
                <div className="flex justify-between"><span className="text-muted-foreground">Bank</span><span className="font-medium">{inquiry?.bank_name}</span></div>
                <div className="flex justify-between"><span className="text-muted-foreground">No. Rekening</span><span className="font-mono">{inquiry?.account_number}</span></div>
                <div className="flex justify-between"><span className="text-muted-foreground">Nominal</span><span className="font-semibold tabular-nums">{formatRupiah(amount)}</span></div>
                <div className="flex justify-between"><span className="text-muted-foreground">Biaya Admin</span><span className="font-medium text-emerald-600">Gratis</span></div>
                {note && <div className="flex justify-between"><span className="text-muted-foreground">Catatan</span><span className="font-medium text-right">{note}</span></div>}
                <div className="border-t pt-2.5 flex justify-between"><span className="font-semibold">Total</span><span className="font-heading font-bold tabular-nums" data-testid="transfer-total-amount">{formatRupiah(amount)}</span></div>
              </div>
              <label className="flex items-center gap-2 text-sm cursor-pointer">
                <Checkbox checked={saveAccount} onCheckedChange={setSaveAccount} data-testid="transfer-save-account-checkbox" />
                Simpan rekening ini untuk transfer berikutnya
              </label>
              <Button className="w-full h-12 text-base bg-gradient-to-r from-[#2E7CF6] to-[#1FC8A9] hover:opacity-90" onClick={() => setPinOpen(true)} data-testid="transfer-submit-button">
                Transfer Sekarang
              </Button>
            </CardContent>
          </Card>
        </motion.div>
      )}

      <PinDialog open={pinOpen} onOpenChange={setPinOpen} onConfirm={confirmWithPin} />
    </div>
  );
}
